/* eslint-disable no-console */
import { setBusy, clearBusy, openInformationDialog, errorToast, successToast } from '../containers'
import * as basehttp from './'

const useInfoDialog = true
const backendErrorTitle = 'Network Error'

const domparser = new DOMParser()

/**
 * Handles errors in html format.
 *
 * @param {string} htmlMsg The message received from the failed backend call.
 * @param {string} url The url the failed backend call.
 * @returns {string} Error message to display.
 */
const handleHtmlErrorMessage = (htmlMsg, url) => {
  const doc = domparser.parseFromString(htmlMsg, 'text/html')
  const title = doc.getElementsByTagName('title')[0].childNodes[0].nodeValue
  const address = doc.getElementsByTagName('address')[0].childNodes[0].nodeValue
  return `${title}, url=${url}, address=${address}, ${new Date().toString()}`
}

/**
 * Handles an error for a failed backend call.
 *
 * @param {string} error The message received from the failed backend call.
 * @param {string} url The url the failed backend call.
 * @returns {string} Error message to display.
 */
const handleError = (error, url) => {
  console.log('*********************** REST ERROR')
  console.log('error--', error)
  if (error.message === undefined && error.response === undefined) {
    return `API Error: Empty response, url=${url}, ${new Date().toString()}`
  } else if (error.response === undefined) {
    return `API Error: Empty response, msg=${error.message}, url=${url}, ${new Date().toString()}`
  }
  if (error.response.data === undefined) {
    return `API Error: Empty response.data, url=${url}, ${new Date().toString()}`
  }
  let msg = error.response.data.message
  if (msg === undefined) {
    msg = error.response.data
    if (msg.indexOf('<html>') !== -1) {
      msg = handleHtmlErrorMessage(msg, url)
    }
  } else if (error.response.data.error) {
    msg = `Path: ${error.response.data.path}, Error: ${error.response.data.error}, Status: ${error.response.data.status}, ${new Date().toString()}`
  }
  return msg
}

/**
 * Displays an error for a failed backend call.
 *
 * @param {string} networkMessage The message received from the failed backend call.
 * @param {string} url The url the failed backend call.
 * @param {Function} errorMessageFunc Alternative function to display the error message.
 * @returns {void} Nothing
 */
export const displayBackendError = (networkMessage, url, errorMessageFunc) => {
  const msg = handleError(networkMessage, url)
  if (errorMessageFunc) {
    errorMessageFunc(msg)
  } else {
    if (useInfoDialog) {
      openInformationDialog({ noTranslateTitle: true, noTranslateMessage: true, message: msg, title: backendErrorTitle })
    } else {
      errorToast({ noTranslateTitle: true, noTranslateMessage: true, message: msg, title: backendErrorTitle })
    }
  }
}

/**
 * @function uiGet
 * @description
 * Base function to make Api get calls to the backend. Handles displaying the busy cursor and error messages.
 * @param {object} uiGetParams object parameters to uiGet.
 * @param {string | string[] | Promise<any>} uiGetParams.service url string or array of urls or promise based api call.
 * @param {boolean} uiGetParams.allowBusy Indicates whether to use the busy cursor.
 * @param {boolean} uiGetParams.allowErrorMsg Indicates whether to display error messages.
 * @param {Function} uiGetParams.errorMessageFunc Error message display function.
 * @param {Function} uiGetParams.setBusyOverride Set busy function override.
 * @param {Function} uiGetParams.clearBusyOverride Clear busy function override.
 * @returns {Promise<object>} Fetch response as a promise.
 */
export const uiGet = ({ service, allowBusy = true, allowErrorMsg = true, errorMessageFunc, setBusyOverride, clearBusyOverride, }) => {
  return new Promise((resolve, reject) => {
    if (allowBusy) {
      if (setBusyOverride) {
        setBusyOverride()
      } else {
        setBusy('1')
      }
    }
    basehttp.get(service).then(response => {
      if (allowBusy) {
        if (clearBusyOverride) {
          clearBusyOverride()
        } else {
          clearBusy('1')
        }
      }
      resolve(response)
    }).catch(error => {
      if (allowBusy) {
        if (clearBusyOverride) {
          clearBusyOverride()
        } else {
          clearBusy('1')
        }
      }
      if (allowErrorMsg) {
        displayBackendError(error, typeof service === 'string' ? service : '', errorMessageFunc)
        reject(error)
      } else {
        reject(error)
        throw new Error(error)
      }
    })
  })
}

/**
 * @function uiPost
 * @description
 * Base function to make Api post calls to the backend. Handles displaying the busy cursor and error messages.
 * @param {object} uiPostParams Object parameters to uiPost.
 * @param {string} uiPostParams.url api url
 * @param {object} uiPostParams.postObj post body for the api call.
 * @param {boolean} uiPostParams.allowBusy Indicates whether to use the busy cursor.
 * @param {boolean} uiPostParams.allowErrorMsg Indicates whether to display error messages.
 * @param {string} uiPostParams.successMsg Message to display on success.
 * @param {string} uiPostParams.successMsgTitle Message title to display on success.
 * @param {Function} uiPostParams.setBusyOverride Set busy function override.
 * @param {Function} uiPostParams.clearBusyOverride Clear busy function override.
 * @param {Function} uiPostParams.successMessageFunc Success message display function.
 * @param {Function} uiPostParams.errorMessageFunc Error message display function.
 * @returns {Promise<object>} Fetch response as a promise.
 */
export const uiPost = ({ 
  url, 
  postObj, 
  allowBusy = true, 
  allowErrorMsg = true, 
  successMsg,
  successMsgTitle, 
  setBusyOverride, 
  clearBusyOverride, 
  successMessageFunc, 
  errorMessageFunc 
}) => {
  return new Promise((resolve, reject) => {
    if (allowBusy) {
      if (setBusyOverride) {
        setBusyOverride()
      } else {
        setBusy('2')
      }
    }
    basehttp.post(url, postObj).then(response => {
      if (allowBusy) {
        if (clearBusyOverride) {
          clearBusyOverride()
        } else {
          clearBusy('2')
        }
      }
      resolve(response)
      if (successMessageFunc) {
        successMessageFunc()
      } else if (successMsg) {
        if (useInfoDialog) {
          openInformationDialog({ message: successMsg, title: successMsgTitle })
        } else {
          successToast({ message: successMsg, title: successMsgTitle })
        }
      }
    }).catch(error => {
      if (allowBusy) {
        if (clearBusyOverride) {
          clearBusyOverride()
        } else {
          clearBusy('2')
        }
      }
      if (allowErrorMsg) {
        displayBackendError(error, url, errorMessageFunc)
        reject(error)
      } else {
        reject(error)
        throw new Error(error)
      }
    })
  })
}

/**
 * @function uiDel
 * @description
 * Base function to make Api del calls to the backend. Handles displaying the busy cursor and error messages.
 * @param {string | string[]} url url string.
 * @param {boolean} allowBusy Indicates whether to use the busy cursor.
 * @param {boolean} allowErrorMsg Indicates whether to display error messages.
 * @param {boolean} successMsg Indicates whether to display a success message.
 * @returns {Promise<object>} Fetch response as a promise.
 */
export const uiDel = (url, allowBusy = true, allowErrorMsg = true, successMsg) => {
  return new Promise(resolve => {
    if (allowBusy) {
      setBusy('3')
    }
    basehttp.del(url).then(response => {
      if (allowBusy) {
        clearBusy('3')
      }
      if (successMsg) {
        openInformationDialog(successMsg)
      }
      resolve(response)
    }).catch(error => {
      if (allowBusy) {
        clearBusy('3')
      }
      if (allowErrorMsg) {
        displayBackendError(error, url)
      }
    })
  })
}
